import { PromoListItem } from "@/core/entities/promotion/PromoListItem";
import { NumberFormatType } from "@/core/types";
import { SkeletonCell } from "@/presentation/ui/components/common";
import { Highlighter, Span } from "@ir/ui";
import { PromoStatusBadge } from "./PromoStatusBadge";

interface PromoNameCellProps {
  record: PromoListItem;
  isRefreshing: boolean;
  searchKeywords: string[];
}

export const PromoNameCell = ({ record, isRefreshing, searchKeywords }: PromoNameCellProps) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  return (
    <Span className="text-truncate d-block" title={record.promoName}>
      <Highlighter keywords={searchKeywords}>{record.promoName}</Highlighter>
    </Span>
  );
};

interface PromoTypeCellProps {
  record: PromoListItem;
  isRefreshing: boolean;
}

export const PromoTypeCell = ({ record, isRefreshing }: PromoTypeCellProps) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  return <Span>{record.type.promoTypeName}</Span>;
};

interface PurchaserCellProps {
  record: PromoListItem;
  isRefreshing: boolean;
  searchKeywords: string[];
}

const renderPurchaserContent = (record: PromoListItem, searchKeywords: string[]) => {
  if (record.purchaserName) {
    return <Highlighter keywords={searchKeywords}>{record.purchaserName}</Highlighter>;
  }

  return <Span>—</Span>;
};

export const PurchaserCell = ({ record, isRefreshing, searchKeywords }: PurchaserCellProps) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  return renderPurchaserContent(record, searchKeywords);
};

interface DateRangeCellProps {
  record: PromoListItem;
  isRefreshing: boolean;
  dateFormat: string;
}

export const DateRangeCell = ({ record, isRefreshing, dateFormat }: DateRangeCellProps) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  return <Span>{record.dateRangeFormatted(dateFormat)}</Span>;
};

interface ClaimedCellProps {
  record: PromoListItem;
  isRefreshing: boolean;
  numberFormat?: NumberFormatType;
}

export const ClaimedCell = ({ record, isRefreshing, numberFormat }: ClaimedCellProps) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  const format = numberFormat ?? "accounting";
  return <Span>{record.displayClaimed(format)}</Span>;
};

interface MatchesCellProps {
  record: PromoListItem;
  isRefreshing: boolean;
}

export const MatchesCell = ({ record, isRefreshing }: MatchesCellProps) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  return <Span>{record.displayMatches()}</Span>;
};

interface StatusCellProps {
  record: PromoListItem;
  isRefreshing: boolean;
}

export const StatusCell = ({ record, isRefreshing }: StatusCellProps) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  return <PromoStatusBadge statusName={record.status.promoStatusName} statusKey={record.status.promoStatusKey} />;
};
